import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { faAngleRight, faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import backgroundImage from 'assets/images/home-background.jpg';
import axios from 'axios';
import Logo from 'components/Logo';
import jwtDecode from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { routes } from 'routes';
import { signIn, tokenToUser } from 'state/auth';
import styled, { css } from 'styled-components';
import { colors } from 'styles/colors';
import { devices } from 'styles/devices';
import MainTemplate from 'templates/MainTemplate/MainTemplate';
import PasswordChanged from 'pages/SignInView/PasswordChanged';
import { SwitchLang } from 'components/SwitchLang';

const useQuery = () => new URLSearchParams(useLocation().search);

const SignInView = () => {
   const dispatch = useDispatch();
   const nav = useNavigate();
   const location = useLocation();
   const passwordChanged = useQuery().get('password_changed');
   const user = useSelector((s) => s.auth.user);
   const [username, setUsername] = useState('');
   const [password, setPassword] = useState('');
   const [info, setInfo] = useState('');
   const intl = useIntl();

   const handleSubmit = (e) => {
      e.preventDefault();
      axios
         .post('/jwt-auth/v1/token', {
            username,
            password
         })
         .then(({ data }) => {
            localStorage.setItem('token', data.token);
            axios.defaults.headers['Authorization'] = `Bearer ${data.token}`;
            const t = jwtDecode(data.token);
            const u = tokenToUser(t);
            dispatch(signIn(u));
            if (u.consentApprovedAt) {
               nav(location.state?.from.pathname || routes.catalog);
            } else {
               nav(routes.catalogConsent, {
                  state: {
                     username,
                     password
                  }
               });
            }
         })
         .catch((err) => {
            if (err.response?.data?.message) {
               setInfo(err.response?.data.message);
               return;
            }
            setInfo('Wystąpił błąd');
            throw err;
         });
   };

   if (user) {
      if (!user.consentApprovedAt) {
         return (
            <Navigate
               to={routes.catalogConsent}
               state={{
                  username,
                  password
               }}
            />
         );
      }
      return <Navigate to={routes.catalog} />;
   }

   return (
      <MainTemplate>
         <Wrapper>
            <Header>
               <Logo />
               <div style={{ float: 'right', marginLeft: 'auto' }}>
                  <SwitchLang />
               </div>
            </Header>
            {passwordChanged && <PasswordChanged />}
            <InnerWrapper passwordChanged>
               <Form onSubmit={handleSubmit}>
                  <div>
                     <Title>{intl.formatMessage({ id: 'CatalogConsentView.Title' })}</Title>
                  </div>
                  <InputContainer>
                     <FontAwesomeIcon icon={faUser} style={IconStyle} />
                     <Input
                        type="text"
                        name={'username'}
                        className="form-control"
                        onChange={(e) => setUsername(e.currentTarget.value)}
                        placeholder={intl.formatMessage({ id: 'CatalogConsentView.User' })}
                        value={username}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                     />
                  </InputContainer>
                  <InputContainer>
                     <FontAwesomeIcon icon={faLock} style={IconStyle} />
                     <Input
                        type="password"
                        name={'password'}
                        className="form-control"
                        onChange={(e) => setPassword(e.currentTarget.value)}
                        placeholder={intl.formatMessage({ id: 'CatalogConsentView.Password' })}
                        value={password}
                     />
                  </InputContainer>
                  <ButtonContainer>
                     <LoginButton className={'btn btn-danger'}>
                        <span>{intl.formatMessage({ id: 'CatalogConsentView.Login' })}</span>
                        <FontAwesomeIcon icon={faAngleRight} />
                     </LoginButton>
                  </ButtonContainer>
                  <div>
                     <StyledButton
                        className={'btn btn-link'}
                        href={'https://info-cms.konsport.com/zmien-haslo'}
                     >
                        {intl.formatMessage({ id: 'CatalogConsentView.WrongPassword' })}
                     </StyledButton>
                  </div>
                  {info && (
                     <ResponseContainer>
                        <span>{info}</span>
                     </ResponseContainer>
                  )}
               </Form>
               <Background>
                  <Image src={backgroundImage} alt="" />
               </Background>
            </InnerWrapper>
         </Wrapper>
      </MainTemplate>
   );
};

const Input = styled.input`
   background: #f2f2f2;
   border-radius: 22px;
   border: none;
   padding-left: 2.5rem;

   &:focus {
      background: #f2f2f2;
      outline: none;
      border: none;
      box-shadow: none;
   }
`;

const InputContainer = styled.div`
   position: relative;
   margin-bottom: 0.5rem;
`;

const ButtonContainer = styled.div`
   display: flex;
   flex-direction: row;
   align-content: center;
   justify-content: flex-end;
   margin: 1rem 0 1.5rem 0;

   ${devices.tablet} {
      justify-content: flex-start;
   }
`;

const LoginButton = styled.button`
   border-radius: 22px;
   padding: 0.25rem 2.5rem;

   span {
      margin-right: 1rem;
   }
`;

const Image = styled.img`
   width: 100%;
   border-radius: 22px;
`;

const ResponseContainer = styled.div`
   margin-top: 2rem;
   color: ${colors.primary};
`;

export const IconStyle = {
   position: 'absolute',
   opacity: '0.5',
   top: '10px',
   left: '15px'
};

const Title = styled.h1`
   font-size: 2rem;
   font-weight: 700;
   margin-bottom: 2rem;

   ${devices.tabletXL} {
      font-size: 3rem;
   }
`;

const Wrapper = styled.div`
   padding: 20px;

   ${devices.tabletL} {
      padding: 0 60px;
   }

   ${devices.tabletXL} {
      padding: 0 100px;
   }
`;

const InnerWrapper = styled.div`
   width: 100%;
   margin-top: 10vh;
   max-width: 360px;
   flex: 1;
   display: flex;

   ${(props) =>
      props.passwordChanged
         ? css`
              margin-top: 4vh;
           `
         : css`
              margin-top: 10vh;
           `}

   ${devices.mobile} {
      max-width: 100%;
      ${(props) =>
         props.passwordChanged
            ? css`
                 margin: 10vh auto;
              `
            : css`
                 margin: 20vh auto;
              `}
   }

   ${devices.desktop} {
      max-width: 1140px;
      ${(props) =>
         props.passwordChanged
            ? css`
                 margin: 10vh auto;
              `
            : css`
                 margin: 15vh auto;
              `}
   }
`;

const Form = styled.form`
   width: 100%;
   background: #fff;
   padding: 2rem;
   position: relative;
   border-radius: 2rem;

   ${devices.tablet} {
      width: 30%;
      &:after {
         content: '';
         background: #fff;
         position: absolute;
         top: 0;
         left: 100%;
         width: 4rem;
         height: 100%;
      }
      border-top-right-radius: initial;
      border-bottom-right-radius: initial;
   }

   ${devices.desktop} {
      display: flex;
      flex-direction: column;
   }
`;

const Background = styled.div`
   display: none;
   width: 70%;
   z-index: 10;
   border-top-right-radius: 2rem;
   border-bottom-right-radius: 2rem;

   ${devices.tablet} {
      display: flex;
   }
`;

const StyledButton = styled.a`
   color: ${colors.neutral60};

   &:hover {
      color: ${colors.primary};
   }
`;

const Header = styled.header`
   min-height: 40px;
   display: flex;
   align-items: center;

   ${devices.desktop} {
      padding: 30px 0;
   }

   ${devices.desktopFHD} {
      padding: 50px 0;
   }
`;

export default SignInView;
